import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useDebounce } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { createSkillAdf } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { usePlaceholderStore } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { getIconColor } from '@atlassian/rovo-platform-ui-components/skills/skill-colors';
import {
	DefaultSkillIcon,
	skillIconMap,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillHoverCardContent } from '@atlassian/rovo-platform-ui-components/skills/SkillHoverCardContent';
import { parseUniversalSkillEdges } from '@atlassian/rovo-skills/utils/parse-skill';
import type { Space } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { useSearchSpaces } from '@atlassian/rovo-spaces/services/useSearchSpaces';
import { createEmojiProvider } from '@atlassian/rovo-spaces/utils/emoji-provider';

import type { AutoSuggestFloatingMenuItem } from '../../ui/AutoSuggestFloatingMenu';
import { AutoSuggestScreen, type AutoSuggestItem } from '../../ui/AutoSuggestScreen';

import type { autoSuggestSkills_AvailableSkillsQuery } from './__generated__/autoSuggestSkills_AvailableSkillsQuery.graphql';
import type { autoSuggestSkills_Query } from './__generated__/autoSuggestSkills_Query.graphql';

// Lazy-load AutoSuggestFloatingMenu (and its @atlaskit/emoji transitive dep) so users outside the
// Rovo Spaces experiment never pay the bundle cost.
const AutoSuggestFloatingMenu = React.lazy(() =>
	import(
		/* webpackChunkName: "async-auto-suggest-floating-menu" */ '../../ui/AutoSuggestFloatingMenu'
	).then((m) => ({
		default: m.AutoSuggestFloatingMenu,
	})),
);

const overlayStyles = cssMap({
	overlay: {
		position: 'absolute',
		top: '0',
		right: '0',
		bottom: '0',
		left: '0',
		overflowX: 'hidden',
		overflowY: 'hidden',
		pointerEvents: 'none',
		zIndex: 1,
	},
	// Variant used when the floating menu is active. Insets left/right to
	// match the chat input's `marginInline: space.200` so the menu is
	// exactly as wide as the input box. No overflow clipping so the
	// menu's border renders cleanly. Uses flex column + justify-content
	// flex-end so the menu anchors to the bottom of the overlay (just
	// above the chat input) rather than the top.
	overlayFloating: {
		position: 'absolute',
		top: '0',
		right: token('space.200'),
		bottom: token('space.075'),
		left: token('space.200'),
		pointerEvents: 'none',
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
	},
	// Floating menu in fullscreen — the overlay starts at the top of
	// `fullscreenPromptArea` (which sits directly below the chat input).
	// `alignItems: center` combined with the menu's own `maxWidth` keeps it
	// the same width as the input. The 16px gap is applied via `paddingTop`
	// on this container rather than `calc()` (which cssMap doesn't support).
	overlayFloatingFullscreen: {
		position: 'absolute',
		top: '0',
		right: '0',
		left: '0',
		paddingTop: token('space.200'),
		pointerEvents: 'none',
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	overlayFullscreen: {
		position: 'absolute',
		top: '0',
		right: '0',
		left: '0',
		height: 'auto',
		maxHeight: 'min(28rem, 55vh)',
		overflowX: 'hidden',
		overflowY: 'auto',
		pointerEvents: 'auto',
		zIndex: 1,
	},
	fullscreenContainer: {
		position: 'absolute',
		top: '0',
		right: '0',
		left: '0',
		bottom: '0',
		backgroundColor: token('elevation.surface'),
		pointerEvents: 'auto',
		zIndex: 1,
	},
	panel: {
		height: '100%',
		width: '100%',
		backgroundColor: token('elevation.surface'),
		pointerEvents: 'auto',
	},
	panelFullscreen: {
		width: '100%',
		maxWidth: 'min(766px, 95vw)',
		marginLeft: 'auto',
		marginRight: 'auto',
		pointerEvents: 'auto',
		overflowX: 'visible',
		overflowY: 'visible',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
});

type SkillIconComponentProps = { label?: string; color?: string };

const SkillIcon = ({ iconName, iconColor }: { iconName?: string; iconColor?: string }) => {
	const IconComponent =
		iconName && iconName in skillIconMap
			? (skillIconMap as Record<string, React.ComponentType<SkillIconComponentProps>>)[iconName]
			: (DefaultSkillIcon as React.ComponentType<SkillIconComponentProps>);

	return <IconComponent label="" {...(iconColor && { color: iconColor })} />;
};

type SkillsContentProps = {
	siteId: string;
	/**
	 * Product key used for the spaces listing (e.g. `'jira'`, `'confluence'`).
	 * Required when the Rovo Spaces experiment is on; otherwise unused.
	 */
	product?: string;
	/**
	 * Experience id used for the spaces listing. Required when the
	 * Rovo Spaces experiment is on; otherwise unused.
	 */
	experienceId?: string;
	/**
	 * Pre-built emoji provider from the host (e.g. townsquare's cached instance).
	 * When provided, avoids a duplicate network request. Falls back to
	 * `createEmojiProvider(siteId)` only if omitted.
	 */
	emojiProvider?: Promise<EmojiProvider>;
	searchQuery: string;
	/**
	 * Debounced version of `searchQuery` for network requests. Callers that
	 * perform server-side filtering (Relay queries, REST searches) should use
	 * this to avoid a request per keystroke. `searchQuery` remains the raw
	 * value for client-side filtering which needs to stay responsive.
	 */
	debouncedSearchQuery: string;
	isSlideVisible: boolean;
	isSpacesEnabled: boolean;
	onSelectSkill: (skill: Skill) => void;
	onSelectSpace: (space: Space) => void;
	onViewAllSkills: () => void;
	onDismiss: () => void;
	onSkillsLoaded: (payload: { hasItems: boolean }) => void;
	layout: 'sidebar' | 'fullscreen';
	experienceName:
		| typeof ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_LEGACY
		| typeof ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_UNIFIED;
};

function SkillsContentRenderer({
	skillsResponse,
	siteId,
	product,
	experienceId,
	emojiProvider: emojiProviderProp,
	searchQuery,
	debouncedSearchQuery,
	isSlideVisible,
	isSpacesEnabled,
	onSelectSkill,
	onSelectSpace,
	onViewAllSkills,
	onDismiss,
	onSkillsLoaded,
	layout,
	experienceName,
}: SkillsContentProps & {
	skillsResponse:
		| NonNullable<autoSuggestSkills_Query['response']>['graphIntegration_componentDirectoryItems']
		| NonNullable<autoSuggestSkills_AvailableSkillsQuery['response']>['convoai_availableSkills'];
}): JSX.Element | null {
	if (skillsResponse) {
		// Legacy error format
		if ('error' in skillsResponse && skillsResponse.error) {
			const err = skillsResponse.error as {
				message?: string;
				extensions?: Array<{ statusCode?: number; errorType?: string }>;
			};
			const errExtensions = err.extensions
				? `[${err.extensions
						?.map(
							(ext: { statusCode?: number; errorType?: string }) =>
								`${ext?.statusCode} - ${ext?.errorType}`,
						)
						?.join(', ')}] `
				: '';
			throw new Error(
				err.message || errExtensions ? `${errExtensions}${err.message}` : 'Failed to load skills',
			);
		}

		// Unified skills error format
		if ('errors' in skillsResponse && skillsResponse.errors && skillsResponse.errors.length > 0) {
			const errorMessages = skillsResponse.errors.map((err) => err?.message).join(', ');
			throw new Error(errorMessages || 'Failed to load skills');
		}
	}

	const skills: Skill[] = React.useMemo(
		() => parseUniversalSkillEdges(skillsResponse?.edges ?? null),
		[skillsResponse?.edges],
	);

	const skillSlugMap = useMemo(() => new Map(skills.map((s) => [s.slug, s])), [skills]);

	// Spaces are gated separately from skills. When the gate is off we pass
	// `skip: true` so no search request is issued.
	// `debouncedSearchQuery` is debounced by the caller (SkillsContentUnified /
	// SkillsContentLegacy) so a single 300 ms timer covers both this REST call
	// and the Relay skills query — no need to debounce again here.
	const { spaces } = useSearchSpaces({
		cloudId: siteId,
		product: product ?? '',
		experienceId: experienceId ?? '',
		searchQuery: debouncedSearchQuery,
		skip: !isSpacesEnabled || !product || !experienceId,
	});

	const spaceIdMap = useMemo(() => new Map(spaces.map((s) => [s.id, s])), [spaces]);

	const emojiProvider = useMemo(
		() => (isSpacesEnabled ? (emojiProviderProp ?? createEmojiProvider(siteId)) : undefined),
		[isSpacesEnabled, emojiProviderProp, siteId],
	);

	// Items for the legacy `AutoSuggestScreen` — skills only, no `kind`/`emoji`.
	const skillItems: AutoSuggestItem[] = useMemo(
		() =>
			skills.map((skill) => ({
				id: skill.slug,
				name: skill.name,
				description: skill.description,
				icon: <SkillIcon iconName={skill.iconName} iconColor={getIconColor(skill.color)} />,
			})),
		[skills],
	);

	// Items for the new floating-menu typeahead — skills + spaces, tagged with
	// `kind` so the menu can render them under separate section headings.
	const floatingMenuItems: AutoSuggestFloatingMenuItem[] = useMemo(
		() => [
			...skills.map((skill) => ({
				id: skill.slug,
				kind: 'skill' as const,
				name: skill.name,
				description: skill.description,
				icon: <SkillIcon iconName={skill.iconName} iconColor={getIconColor(skill.color)} />,
			})),
			...(isSpacesEnabled
				? spaces.map((space) => ({
						id: space.id,
						kind: 'space' as const,
						name: space.name,
						description: space.description ?? undefined,
						emoji: space.emoji ?? undefined,
						icon: <FolderClosedIcon label="" color={token('color.text.subtlest')} />,
					}))
				: []),
		],
		[skills, spaces, isSpacesEnabled],
	);

	const onSelectSkillItem = useCallback(
		(item: AutoSuggestItem) => {
			const skill = skillSlugMap.get(item.id);
			if (skill) onSelectSkill(skill);
		},
		[skillSlugMap, onSelectSkill],
	);

	const onSelectFloatingMenuItem = useCallback(
		(item: AutoSuggestFloatingMenuItem) => {
			if (item.kind === 'space') {
				const space = spaceIdMap.get(item.id);
				if (space) onSelectSpace(space);
				return;
			}
			const skill = skillSlugMap.get(item.id);
			if (skill) onSelectSkill(skill);
		},
		[skillSlugMap, spaceIdMap, onSelectSkill, onSelectSpace],
	);

	const renderHoverCardContent = useCallback(
		(item: AutoSuggestItem) => {
			const skill = skillSlugMap.get(item.id);
			if (!skill) return null;
			const { slug: _slug, ...skillContentProps } = skill;
			return <SkillHoverCardContent {...skillContentProps} />;
		},
		[skillSlugMap],
	);

	// Visibility check considers whichever list will be rendered. When the
	// spaces gate is on, the floating menu surfaces both skills and spaces, so
	// the typeahead stays open if either category matches the current query.
	const hasItems = useMemo(() => {
		const sourceItems = isSpacesEnabled ? floatingMenuItems : skillItems;
		if (!searchQuery.trim()) return sourceItems.length > 0;
		const search = searchQuery.toLowerCase();
		return sourceItems.some(
			(item) =>
				item.name.toLowerCase().includes(search) ||
				(item.description?.toLowerCase().includes(search) ?? false),
		);
	}, [floatingMenuItems, skillItems, isSpacesEnabled, searchQuery]);

	useEffect(() => {
		onSkillsLoaded?.({ hasItems });
	}, [hasItems, onSkillsLoaded]);

	if (!isSlideVisible) {
		return null;
	}

	const isFullscreen = layout === 'fullscreen';

	// The floating menu positions itself absolutely within the overlay and
	// must NOT be wrapped in the opaque full-height `panel` div — that div
	// fills the entire overlay with `elevation.surface`, which clips the
	// input's drop-shadow and creates a hard white edge at the menu boundary.
	if (isSpacesEnabled) {
		return (
			<>
				<ExperienceSuccess name={experienceName} />
				<Suspense fallback={null}>
					<AutoSuggestFloatingMenu
						items={floatingMenuItems}
						searchQuery={searchQuery}
						onSelectItem={onSelectFloatingMenuItem}
						onDismiss={onDismiss}
						emojiProvider={emojiProvider}
						layout={layout}
					/>
				</Suspense>
			</>
		);
	}

	return (
		<Box xcss={isFullscreen ? overlayStyles.panelFullscreen : overlayStyles.panel}>
			{/*
			 * Mark the auto-suggest experience as successful once data is
			 * available and SkillsContent renders. This emits an Operational
			 * analytics event independent of UFO TTAI — useful for tracking
			 * P95 latency and success rate without contributing to Rovo Chat's
			 * page-level TTAI.
			 */}
			<ExperienceSuccess name={experienceName} />
			<AutoSuggestScreen
				items={skillItems}
				searchQuery={searchQuery}
				onSelectItem={onSelectSkillItem}
				onDismiss={onDismiss}
				onViewAllSkills={isFullscreen ? undefined : onViewAllSkills}
				renderHoverCardContent={renderHoverCardContent}
				layout={layout}
			/>
		</Box>
	);
}

function SkillsContentLegacy({
	siteId,
	product,
	experienceId,
	emojiProvider,
	searchQuery,
	debouncedSearchQuery,
	isSlideVisible,
	isSpacesEnabled,
	onSelectSkill,
	onSelectSpace,
	onViewAllSkills,
	onDismiss,
	onSkillsLoaded,
	layout,
	experienceName,
}: SkillsContentProps): JSX.Element | null {
	const skillsResponse = useLazyLoadQuery<autoSuggestSkills_Query>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseSkillEdges */
		graphql`
			query autoSuggestSkills_Query(
				$first: Int
				$after: String
				$dimensionIds: [ID!]
				$searchKeyword: String
				$relevantFor: [GraphIntegrationDirectoryItemType!] = [SKILL]
				$cloudId: ID!
				$surface: GraphIntegrationSurface
				$shouldFetch: Boolean = true
			) {
				graphIntegration_componentDirectoryItems(
					first: $first
					after: $after
					dimensionIds: $dimensionIds
					searchKeyword: $searchKeyword
					relevantFor: $relevantFor
					cloudId: $cloudId
					surface: $surface
				)
					@optIn(to: "GraphIntegrationItemsQuery")
					@connection(key: "Skills__graphIntegration_componentDirectoryItems")
					@include(if: $shouldFetch) {
					edges {
						node {
							__typename
							... on GraphIntegrationSkillDirectoryItem {
								id
								slashCommand
								name
								description
								iconUrl
								integrationKey {
									displayName
									iconUrl
								}
								color
							}
						}
					}
					error {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: 100,
			cloudId: siteId,
			surface: 'ROVO',
		},
	)?.graphIntegration_componentDirectoryItems;

	return (
		<SkillsContentRenderer
			skillsResponse={skillsResponse}
			siteId={siteId}
			product={product}
			experienceId={experienceId}
			emojiProvider={emojiProvider}
			searchQuery={searchQuery}
			debouncedSearchQuery={debouncedSearchQuery}
			isSlideVisible={isSlideVisible}
			isSpacesEnabled={isSpacesEnabled}
			onSelectSkill={onSelectSkill}
			onSelectSpace={onSelectSpace}
			onViewAllSkills={onViewAllSkills}
			onDismiss={onDismiss}
			onSkillsLoaded={onSkillsLoaded}
			layout={layout}
			experienceName={experienceName}
		/>
	);
}

function SkillsContentUnified({
	siteId,
	product,
	experienceId,
	emojiProvider,
	searchQuery,
	debouncedSearchQuery,
	isSlideVisible,
	isSpacesEnabled,
	onSelectSkill,
	onSelectSpace,
	onViewAllSkills,
	onDismiss,
	onSkillsLoaded,
	layout,
	experienceName,
}: SkillsContentProps): JSX.Element | null {
	// When the spaces gate is off we fall back to AutoSuggestScreen which
	// filters client-side, so pass null as searchKeyword to avoid a network
	// request per keystroke. When the gate is on, use the debounced value
	// (already computed by the caller) for server-side filtering.
	const debouncedKeyword = isSpacesEnabled
		? debouncedSearchQuery.trim().length > 0
			? debouncedSearchQuery.trim()
			: null
		: null;

	const skillsResponse = useLazyLoadQuery<autoSuggestSkills_AvailableSkillsQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseUniversalSkillEdges */
		graphql`
			query autoSuggestSkills_AvailableSkillsQuery(
				$first: Int
				$after: String
				$cloudId: ID!
				$searchKeyword: String
				$shouldFetch: Boolean = false
			) {
				convoai_availableSkills(
					first: $first
					after: $after
					cloudId: $cloudId
					searchKeyword: $searchKeyword
				)
					@connection(key: "AutoSuggestSkills__convoai_availableSkills")
					@optIn(to: "ConvoAiAvailableSkills")
					@include(if: $shouldFetch) {
					edges {
						node {
							__typename
							id
							name
							description
							displayName
							slashCommand
							iconUrl
							iconKey
							helpText
							color
							source
							tags
							category
							integrationKeys
							integrations {
								key
								id
								displayName
								iconUrl
							}
							version
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					totalCount
					metadata {
						anthropicCount
						stratusCount
						isDegraded
					}
					errors {
						message
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: 100,
			cloudId: siteId,
			searchKeyword: debouncedKeyword,
			shouldFetch: true,
		},
	)?.convoai_availableSkills;

	return (
		<SkillsContentRenderer
			skillsResponse={skillsResponse}
			siteId={siteId}
			product={product}
			experienceId={experienceId}
			emojiProvider={emojiProvider}
			searchQuery={searchQuery}
			debouncedSearchQuery={debouncedSearchQuery}
			isSlideVisible={isSlideVisible}
			isSpacesEnabled={isSpacesEnabled}
			onSelectSkill={onSelectSkill}
			onSelectSpace={onSelectSpace}
			onViewAllSkills={onViewAllSkills}
			onDismiss={onDismiss}
			onSkillsLoaded={onSkillsLoaded}
			layout={layout}
			experienceName={experienceName}
		/>
	);
}

function SkillsContent(
	props: Omit<SkillsContentProps, 'debouncedSearchQuery'>,
): JSX.Element | null {
	// Debounce once here so both SkillsContentLegacy (spaces REST search) and
	// SkillsContentUnified (Relay skills query) share a single 300 ms timer
	// rather than each maintaining their own independent debounce.
	const [debouncedSearchQuery] = useDebounce(props.searchQuery, 300);
	const propsWithDebounce = { ...props, debouncedSearchQuery };
	if (fg('rovo_chat_unified_skills_agg')) {
		return <SkillsContentUnified {...propsWithDebounce} />;
	}
	return <SkillsContentLegacy {...propsWithDebounce} />;
}

export type AutoSuggestSkillsRef = {
	dismiss: () => void;
};

export type AutoSuggestSkillsProps = {
	searchQuery: string;
	siteId: string;
	/**
	 * Product key (e.g. `'jira'`, `'confluence'`). Required for the spaces
	 * section of the unified typeahead; when omitted, spaces are not shown.
	 */
	product?: string;
	/**
	 * Experience id passed to `useListSpaces`. Required for the spaces section
	 * of the unified typeahead; when omitted, spaces are not shown.
	 */
	experienceId?: string;
	/**
	 * Pre-built emoji provider from the host. When provided, reuses the
	 * host's cached instance (e.g. townsquare's `getEmojiProviderForCloudId`)
	 * to avoid a duplicate network request. Falls back to `createEmojiProvider`
	 * if omitted.
	 */
	emojiProvider?: Promise<EmojiProvider>;
	onViewAllSkills: () => void;
	onVisibleChange?: (visible: boolean) => void;
	onDismissHandle?: (handle: AutoSuggestSkillsRef | null) => void;
	layout?: 'sidebar' | 'fullscreen';
};

export function AutoSuggestSkills({
	searchQuery,
	siteId,
	product,
	experienceId,
	emojiProvider,
	onViewAllSkills,
	onVisibleChange,
	onDismissHandle,
	layout = 'sidebar',
}: AutoSuggestSkillsProps): JSX.Element {
	const [, { setPlaceholder }] = usePlaceholderStore();
	const { sendAnalyticsEvent } = useAnalytics();
	const { setSelectedSpace, setActiveMenu } = useUIStoreActions();
	// Used to record the skill selection source on the AnalyticsPropertiesContext so
	// the chat input can attach it to the next `aiResult viewed` event. The provider
	// is mounted once per chat instance, so writes here are scoped to the same input
	// that will eventually consume them at submit time.
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();

	const [hasItems, setHasItems] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);
	const isQueryActive = searchQuery.trim().length > 0;

	const isSlideVisible = hasItems && !isDismissed && isQueryActive;
	const prevSlideVisibleRef = useRef(isSlideVisible);

	const dismiss = useCallback(() => setIsDismissed(true), []);

	useEffect(() => {
		if (!onDismissHandle) {
			return;
		}
		onDismissHandle({ dismiss });
		return () => {
			onDismissHandle(null);
		};
	}, [onDismissHandle, dismiss]);

	// Only un-dismiss when the user starts typing a new non-empty query.
	// Resetting on every searchQuery change (including the clear-to-"" on submit)
	// would cause the menu to reappear immediately after the user submits without
	// having selected an item.
	const prevSearchQueryRef = useRef(searchQuery);
	useEffect(() => {
		const prev = prevSearchQueryRef.current;
		prevSearchQueryRef.current = searchQuery;
		if (searchQuery.trim().length > 0 && searchQuery !== prev) {
			setIsDismissed(false);
		}
	}, [searchQuery]);

	useEffect(() => {
		if (prevSlideVisibleRef.current !== isSlideVisible) {
			prevSlideVisibleRef.current = isSlideVisible;
			onVisibleChange?.(isSlideVisible);
		}
	}, [isSlideVisible, onVisibleChange]);

	const handleSkillsLoaded = useCallback((payload: { hasItems: boolean }) => {
		setHasItems(payload.hasItems);
	}, []);

	const handleViewAllSkills = useCallback(() => {
		setIsDismissed(true);
		onViewAllSkills();
	}, [onViewAllSkills]);

	const handleSelectSkill = useCallback(
		(skill: Skill) => {
			setIsDismissed(true);
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoChatSkillSelected',
				attributes: {
					slashCommand: skill.slug,
					skillProductKey: skill.product,
					skillSelectionSource: 'autoSuggest',
				},
			});
			setAnalyticsPropertiesWithoutRerender({ skillSelectionSource: 'autoSuggest' });
			// Place the cursor *after* the skill tag and its trailing space so the
			// user can keep typing immediately. The skill ADF is:
			//   paragraph( inlineExtension(skillTag), text(' ') )
			// In ProseMirror positions, that maps to:
			//   0: before paragraph
			//   1: before the inline extension (skill tag)
			//   2: after the inline extension
			//   3: after the trailing space (the desired caret position)

			setPlaceholder({
				prompt: '',
				adfDocument: createSkillAdf(skill),
				type: 'generic',
				selectionStartIndex: 3,
				selectionEndIndex: 3,
				insertAtCursor: false,
			});
		},
		[sendAnalyticsEvent, setAnalyticsPropertiesWithoutRerender, setPlaceholder],
	);

	const handleSelectSpace = useCallback(
		(space: Space) => {
			setIsDismissed(true);
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoChatSpaceSelected',
				attributes: {
					spaceId: space.id,
					spaceSelectionSource: 'autoSuggest',
				},
			});
			// Set the currently selected space so the landing page (and any
			// observers like the selected-space bar) pick up the new context.
			setSelectedSpace({
				id: space.id,
				name: space.name,
				emoji: space.emoji ?? undefined,
				entryPoint: 'typeahead',
			});
			setActiveMenu('space-landing');
		},
		[sendAnalyticsEvent, setSelectedSpace, setActiveMenu],
	);

	const handleClose = useCallback(() => {
		setIsDismissed(true);
	}, []);

	// Determine which experience name to track based on the active query path.
	// Read once on mount so start/success/failure all use the same name even if
	// the gate flips mid-session (which it shouldn't, but defensive against
	// double-counting in the cohort dashboard).
	const [experienceName] = useState(() =>
		fg('rovo_chat_unified_skills_agg')
			? ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_UNIFIED
			: ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_LEGACY,
	);
	const isSpacesEnabledOuter = FeatureGates.getExperimentValue(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	);

	const content = (
		<Box
			testId="auto-suggest-overlay"
			xcss={
				layout === 'sidebar'
					? isSpacesEnabledOuter
						? overlayStyles.overlayFloating
						: overlayStyles.overlay
					: isSpacesEnabledOuter
						? overlayStyles.overlayFloatingFullscreen
						: overlayStyles.overlayFullscreen
			}
		>
			<ErrorBoundary
				fallbackRender={({ error }) => (
					<ExperienceFailure
						name={experienceName}
						error={error instanceof Error ? error : new Error(String(error))}
					/>
				)}
				onError={(error) => {
					logException({
						exception: error,
						name: 'autoSuggestSkillsRelay',
						tags: { siteId },
					});
					handleClose();
				}}
			>
				<ExperienceStart name={experienceName} />
				<AtlassianRelayEnvironmentProvider>
					<UFOSegment name="auto-suggest-skills">
						<Suspense fallback={null}>
							<SkillsContent
								siteId={siteId}
								product={product}
								experienceId={experienceId}
								emojiProvider={emojiProvider}
								searchQuery={searchQuery}
								isSlideVisible={isSlideVisible}
								isSpacesEnabled={isSpacesEnabledOuter}
								onSelectSkill={handleSelectSkill}
								onSelectSpace={handleSelectSpace}
								onViewAllSkills={handleViewAllSkills}
								onDismiss={handleClose}
								onSkillsLoaded={handleSkillsLoaded}
								layout={layout}
								experienceName={experienceName}
							/>
						</Suspense>
					</UFOSegment>
				</AtlassianRelayEnvironmentProvider>
			</ErrorBoundary>
		</Box>
	);

	if (layout === 'fullscreen' && isSlideVisible && !isSpacesEnabledOuter) {
		return <Box xcss={overlayStyles.fullscreenContainer}>{content}</Box>;
	}

	return content;
}
